import {
    Api, DatePart, DateUtil, DOMUtil, Logger, LogManager, MixinUtil, ModelRow, ObjectUtil, StringUtil, StyleUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    BlurEvent, Button, ChangeEvent, ClickEvent, ContextMenuCreationEvent, ContextMenuLabelProps, DataSource,
    DataSourceMode, DataSourceRemoteDataChangeEvent, DropdownSelectionEvent, Image, Label, Layout, LayoutProps,
    LookupModelSearchEvent, LookupModelSelectionEvent, Overlay, Panel, Table, TableCell,
    TableRow, TableRowDisplayEvent, TableSelectionEvent, Textbox, ValidationResult
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { RowOrderPlanningProfile } from "../../models/ModelOrderPlanningProfile";
import { OrderPlanningProfileFilter } from "../OrderPlanningProfileFilter";
import { TimelineCalculator } from "./TimelineCalculator";
import { TimelineCard } from "./TimelineCard";
import { TimelineDayHeader } from "./TimelineDayHeader";
import { TimeMarker } from "./TimeMarker";
import { FilterPanel, SecondaryFilterProps } from "../FilterPanel";
import { OrderPlanningSecondaryFilter } from "../OrderPlanningSecondaryFilter";
import { AutogenLayoutOrderPlanningTimeline } from "./autogen/AutogenLayoutOrderPlanningTimeline";
import { OrderPlanningToolPanel, Tools } from "../OrderPlanningToolPanel";
import { MobileCommMessages } from "../../mobilecomm/MobileCommMessages";
import { PlanningUtil } from "../PlanningUtil";
import { OrderPlanningLayout } from "../OrderPlanningLayout";
import { OrderTabView } from "../../OrderTabView";
import { EventCard } from "./EventCard";
import { MovementCard } from "./MovementCard";
import { getDispatchControlValue } from "@mcleod/general";
import { TimelineComponentSearcher } from "./TimelineComponentSearcher";
import { TimelineCardLayout } from "./TimelineCardLayout";
import { TimelineZoomValidator } from "./TimelineZoomValidator";
import { LateRouteViewSlideout } from "../../asset/LateRouteViewSlideout";

const log = LogManager.getLogger("lme.display.planning.timeline.VisibilityPlanner");

enum CardType {
    EVENT,
    MOVEMENT
}

export class OrderPlanningTimeline extends AutogenLayoutOrderPlanningTimeline {
    private adhocProfileFilterInUse = false;
    private initialConfigFinished = false;
    private calculator: TimelineCalculator;
    private stickyColumnArray: number[] = [];
    private movementColumnIndex;
    private headerScrollWidth = -1;
    private bodyTableCellHeight: number;
    private timelineCards: TimelineCard[] = [];
    private selectedCard: TimelineCard;
    private _initialCellLoaded: boolean = false;
    private zoomValidator: TimelineZoomValidator;
    private largeElements = [];
    private timelineFilterPanel: FilterPanel;
    private timelineSecondaryFilters: SecondaryFilterProps[] = [
        OrderPlanningSecondaryFilter.FILTER_DISPATCH_BY_UNASSIGNED,
        OrderPlanningSecondaryFilter.FILTER_DISPATCH_BY_ASSIGNED,
        OrderPlanningSecondaryFilter.FILTER_TRACKING_LATE,
        OrderPlanningSecondaryFilter.FILTER_MC_MESSAGES,
        OrderPlanningSecondaryFilter.FILTER_EQUIP_ARRIVED_AT_SHIPPER,
        OrderPlanningSecondaryFilter.FILTER_ARRIVED_AT_CONSIGNEE,
        OrderPlanningSecondaryFilter.FILTER_TRACKING_LATE,
        OrderPlanningSecondaryFilter.FILTER_TEMP_WARNINGS,
        OrderPlanningSecondaryFilter.FILTER_UNSEATED_TRACTORS,
        OrderPlanningSecondaryFilter.FILTER_FREE_TIME_STARTED,
        OrderPlanningSecondaryFilter.FILTER_DETENTION_CHARGING,
        OrderPlanningSecondaryFilter.FILTER_DETENTION_OCCURRED,
        OrderPlanningSecondaryFilter.FILTER_DETENTION_CLOSED,
    ];

    getThisAsLayout(): Layout {
        return this;
    }

    getTable(): Table {
        return this.tableTimeline;
    }

    getToolsPanel(): OrderPlanningToolPanel {
        return this.layoutOPTimelineTools.toolPanel;
    }

    getLog(): Logger {
        return log;
    }

    async onLoad(): Promise<void> {
        this.buildStickyColumnArray();
        this.setTitles();
        this.timelineFilterPanel = new FilterPanel(this.timelineSecondaryFilters, this.tableTimeline, this.panelTimelineFilters);
        await this.layoutOPTimelineTools.showButtons(Tools.EQUIP_MANAGE_ASSIGN, Tools.DISPATCH, Tools.LOAD_MATCH,
            Tools.DRIVER_EVENT, Tools.VIEW_MOVEMENTS, Tools.ZMIT, Tools.SEND_MESSAGE, Tools.SEAT_TRACTOR, Tools.FEASIBILITY);
        this.layoutOPTimelineTools.panelToolLeft.visible = false;
        this.zoomValidator = new TimelineZoomValidator(this.textboxHours);
        this.tableTimeline.customFilter = (tr: TableRow) => this.filterTimelineItems(tr);
        this.tableTimeline.addlSearcherCallback = (): TimelineComponentSearcher[] => this.tableSearcherCreationCallback();
        this.initTimer();
        this.setupProfileIdListeners();
        this.setupDeadHeadListeners();
        if (this.isNested !== true)
            this.loadProfile();
        else {
            this.panelProfile.visible = false;
        }
        this.element.addEventListener("click", () => this.closeSelectedCard());
        this.addTimelineScrollListener();
    }

    private get startDate(): Date {
        const dataValue = this.textboxStartDate.getDataValue();
        return !StringUtil.isEmptyString(dataValue) ? DateUtil.setToBeginningOfDay(new Date(this.textboxStartDate.getDataValue())) : null;
    }

    private get endDate(): Date {
        const dataValue = this.textboxEndDate.getDataValue();
        return !StringUtil.isEmptyString(dataValue) ? DateUtil.setToEndOfDay(new Date(this.textboxEndDate.getDataValue())) : null;
    }

    get initialCellLoaded(): boolean {
        return this._initialCellLoaded;
    }

    set initialCellLoaded(value: boolean) {
        this._initialCellLoaded = value;
    }

    private buildStickyColumnArray() {
        this.movementColumnIndex = this.tableTimeline.columns.length -1;
        for (let i = 0; i < this.movementColumnIndex; i++) {
            this.stickyColumnArray.push(-1);
        }
    }

    private applyToStickyColumns(callback: (index:number) => void) {
        for (let i = 0; i < this.stickyColumnArray.length; i++) {
            callback(i);
        }
    }

    private setTitles() {
        this.layoutPlanningTitle.majorTitle = "Visual Planner";
        this.layoutPlanningTitle.minorTitle = "Timeline";
    }

    private setCalendarFromProfile() {
        this.textboxStartDate.text = null;
        this.textboxStartDate.displayData(null, null, 0);
        this.textboxStartDate.dateDefault = this.profile.data.calendar_start_web;
        this.validateDate(this.textboxStartDate);

        this.textboxEndDate.text = null;
        this.textboxEndDate.displayData(null, null, 0);
        this.textboxEndDate.dateDefault = this.profile.data.calendar_end_web;
        this.validateDate(this.textboxEndDate);

        this.updateHours(String(this.profile.data.calendar_zoom_web));
    }

    private addDateValidations() {
        this.textboxStartDate.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.validateDate(this.textboxStartDate, mode);
        };
        this.textboxEndDate.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.validateDate(this.textboxEndDate, mode);
        };
    }

    private validateDate(date: Textbox, mode?: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH || (this.startDate == null || this.endDate == null)) {
            return null;
        }
        const result: ValidationResult = { isValid: true, component: date, validationMessage: null };
        const otherComp = date === this.textboxStartDate ? this.textboxEndDate : this.textboxStartDate;
        const dayDiff = DateUtil.dateDiff(DatePart.SECOND, DateUtil.setToBeginningOfDay(this.endDate), DateUtil.setToBeginningOfDay(this.startDate));
        if (dayDiff < 0) {
            result.isValid = false;
            result.validationMessage = "The early date cannot be later than the late date.";
        }

        const beforeStartBound = DateUtil.dateDiff(DatePart.DAY, this.startDate, DateUtil.dateAdd(DatePart.DAY, new Date(), -14)) < 0;
        const afterEndBound = DateUtil.dateDiff(DatePart.DAY, this.endDate, DateUtil.dateAdd(DatePart.DAY, new Date(), 14)) > 0;
        if (date == this.textboxStartDate && beforeStartBound) {
            result.isValid = false;
            result.validationMessage = "The start date cannot be more than 14 days in the past";
        } else if (date == this.textboxEndDate && afterEndBound) {
            result.isValid = false;
            result.validationMessage = "The end date cannot be more than 14 days in the future";
        }

        if (dayDiff >= 0 && !beforeStartBound && !afterEndBound) {
            this.textboxStartDate.tooltip = "Date the visual planner begins";
            this.textboxEndDate.tooltip = "Date the visual planner ends";
            date.validationWarning = null;
            otherComp.validationWarning = null;
        } else {
            date.validationWarning = result.validationMessage;
        }
        return result;
    }

    dateOnBlur(event: BlurEvent) {
        if (!event.changedWhileFocused || this.textboxStartDate.validationWarning || this.textboxEndDate.validationWarning) {
            return;
        }
        this.redisplay();
    }

    dateOnChange(event: ChangeEvent) {
        if (event.newValue.length < 8 || !event.userInitiatedChange) {
            return;
        }
        if ((event.target as Textbox).validateSimple())
            this.redisplay();
    }

    private updateHours(hours: string) {
        this.textboxHours.setSelectedItemFromValue(hours);
    }

    private initCalculator() {
        this.headerScrollWidth = -1;
        const hours: number = Number(this.textboxHours.selectedItem?.value);
        this.calculator = new TimelineCalculator(this.startDate, this.endDate, hours);
    }

    private resetForDates() {
        if (this.initialConfigFinished !== true)
            return;
        this.configureMovementsColumnHeader();
    }

    private getHeaderCell(indexOrId: number | string): TableCell {
        if (typeof indexOrId === "number")
            return this.tableTimeline.tableHeadingRow.cells[indexOrId];
        return this.tableTimeline.tableHeadingRow.cells.find(cell => cell.id === indexOrId);
    }

    private getRowCell(indexOrId: number | string, tableRow: TableRow): TableCell {
        if (typeof indexOrId === "number")
            return tableRow.cells[indexOrId];
        return tableRow.cells.find(cell => cell.id === indexOrId);
    }

    private configureTableInitial() {
        this.calculateLeftPostitions();
        this.configureMovementsColumnHeader();
        this.tableTimeline.addSelectionListener((event: TableSelectionEvent) => {
            this.applyToStickyColumns(index => {
                if (event.oldRow != null) {
                    event.oldRow.cells[index].backgroundColor = "McLeodWhite";
                }
                if (event.newRow != null) {
                    event.newRow.cells[index].backgroundColor = "primary.lightest";
                }
            })
        });
    }

    private calculateLeftPostitions(reset:boolean=false) {
        this.applyToStickyColumns(index => {
            const headerCell = this.getHeaderCell(index);
            if (this.stickyColumnArray[index] == -1 || reset) {
                if (headerCell.id !== "columnHeaderDeadHead" || this.findNearProps != null) {
                    this.stickyColumnArray[index] = this.getCellLeft(headerCell);
                    this.makeCellSticky(headerCell, this.stickyColumnArray[index]);
                    headerCell.setProps({ paddingTop: 0, paddingBottom: 0 });
                }
            }
        })
    }

    private makeCellSticky(cell: TableCell, left: number) {
        cell.style.position = "sticky";
        cell.style.left = StyleUtil.numberAsPixels(left);
    }

    private getCellLeft(cell: TableCell): number {
        let totalWidth = 0;
        for (const currentCell of cell.row.cells) {
            if (currentCell.id === "columnHeaderDeadHead" && this.findNearProps == null)
                continue;
            if (currentCell === cell) {
                return totalWidth;
            } else {
                totalWidth += DOMUtil.getElementWidth(currentCell._element);
            }
        }
    }

    private configureMovementsColumnHeader() {
        let date = new Date(this.calculator.startDate);
        const cell = this.getHeaderCell(this.movementColumnIndex);
        cell.width = null;
        cell.removeAll();
        cell.paddingLeft = 0;
        for (let index=0 ; index<this.calculator.daysDisplayed ; index++) {
            const columnHeader = new TimelineDayHeader(date, this.calculator);
            cell.add(columnHeader);
            date = DateUtil.dateAdd(DatePart.DAY, date, 1);
        }
        // Marker will be added to this cell and positioned below it
        cell._element.style.overflow = "visible";
        cell._element.style.position = "relative";
        this.getHeaderCell(this.movementColumnIndex).setProps({ paddingTop: 0, paddingBottom: 0 });
    }

    private initializeTableCellHeight(cell: TableCell) {
        if (this.bodyTableCellHeight == null)
            this.bodyTableCellHeight = DOMUtil.getElementHeight(cell._element);
    }

    tableTimelineOnDblClick(event: ClickEvent) {
        const row = this.selectedCard ? this.selectedCard.data : null;
        if (row != null && row.get("type") == 0) {
            this.showOrder(row.get("dispatch_id", "none"), row.get("id"));
        }
    }

    tableTimelineOnSelect(event: TableSelectionEvent) {
        this.closeSelectedCard();
    }

    tableTimelineOnRowDisplay(event: TableRowDisplayEvent) {
        this.setupTimelineRow(event.getTableRow());
    }

    setupTimelineRow(tableRow: TableRow) {
        const modelRow: ModelRow = tableRow.data;
        const tractorCell = this.getRowCell("tableTimeline-tractor", tableRow);
        const movementCell = tableRow.cells[this.movementColumnIndex];
        movementCell.components = [];

        this.createTimeMarker();
        this.applyToStickyColumns(index => this.makeCellSticky(this.getRowCell(index, tableRow), this.stickyColumnArray[index]))
        this.initializeTableCellHeight(tableRow.cells[0]);
        this.setDeadInfo(tableRow, modelRow);
        this.generateMovementsColumnBackground(movementCell, tableRow.expanded);
        this.addHoursOfServiceHover(tableRow);
        this.addTooltipHoversToRow(tableRow);
        PlanningUtil.setUpMcMessages(tableRow, modelRow);

        if (!this.initialCellLoaded) {
            this.applyAfterInitialLoad(movementCell);
        }

        this.applyToStickyColumns(index => {
            const cell = this.getRowCell(index, tableRow);
            cell.contextMenuItems = () => this.getToolsPanel().getContextMenuItems(tableRow);
            cell.addContextMenuCreationListener((event: ContextMenuCreationEvent) => {
                this.getToolsPanel().contextMenuCreationEvent(event);
            });
        })

        const movementHeaderCell = this.getHeaderCell(this.movementColumnIndex);
        if (this.headerScrollWidth == -1) {
            this.headerScrollWidth = movementHeaderCell._element.scrollWidth;
        }
        movementHeaderCell.width = this.headerScrollWidth;

        this.applyToStickyColumns(index => {
            const headerCell = this.getHeaderCell(index);
            headerCell.setProps({ paddingTop: 0, paddingBottom: 0 });
        });
        movementHeaderCell.setProps({ paddingTop: 0, paddingBottom: 0 });

        this.getHeaderCell(this.stickyColumnArray.length-1).element.style.boxShadow = "rgba(0, 0, 0, 0.2) 4px 0px 5px -1px";
        this.applyToStickyColumns(index => {
            this.getRowCell(index, tableRow).element.style.boxShadow =
                "rgba(0, 0, 0, 0.2) 4px 0px 5px 0px";
        })

        movementCell.padding = 0;
        movementCell.width = this.headerScrollWidth;
        movementCell.style.position = "relative";
        movementCell.style.overflow = "visible";

        const overlappedData = this.getOverlapping(this.getAllData(tableRow));
        const imageExpand = tableRow.findComponentById("imageExpand");
        imageExpand.visible = !ObjectUtil.isEmptyObject(overlappedData);
        if (imageExpand.visible) {
            this.setupExpanded(tableRow);
        }

        this.createMovementCards(tableRow, overlappedData);
        this.createEventCards(tableRow, overlappedData);
    }

    private applyAfterInitialLoad(cell: TableCell) {
        // Ensure that the movement cell's full width is loaded
        cell.ensureLoaded().then(() => {
            if (this.calculator.todayInRange()) {
                this.scrollToToday();
            } else {
                this.tableTimeline.adhocScroll({ left: 0 });
            }
            const firstRow = this.tableTimeline.rows[0];
            const rowHeightWidth = { height: firstRow.element.offsetHeight, width: firstRow.element.offsetWidth };
            this.tableTimeline.rows.forEach((row: TableRow) => {
                row.placeholderHeightWidth = rowHeightWidth;
            });
        })
        this.initialCellLoaded = true;
    }

    private generateMovementsColumnBackground(cell: TableCell, expanded: boolean) {
        for (let x=0 ; x<this.calculator.blocksPerRow ; x++) {
            cell.add(this.createSingleBackgroundPanel(cell.row.index, x, expanded));
        }
    }

    private createSingleBackgroundPanel(rowIndex: number, blockIndex: number, expanded: boolean): Panel {
        return new Panel({
            backgroundColor: Math.floor(blockIndex/this.calculator.blocksPerDay) % 2 == 0 ? "white" : "backgroundSubtle",
            color: "subtle.dark",
            height: expanded ? 77 : this.bodyTableCellHeight,
            id: `panelBackground-${rowIndex}-${blockIndex}`,
            width: this.calculator.singleBlockWidthString,
            rowBreak: false
        });
    }

    addTimelineScrollListener() {
        this.tableTimeline["_tbody"].addEventListener("scrollend", this.scrollCheck.bind(this));
    }

    scrollCheck() {
        this.largeElements.forEach((e) => {
            this.checkCardIntersectionAndHiddenPortion(e.card, e.card.element, e.stickyColumnElement);
        });
    }

    checkCardIntersectionAndHiddenPortion(movementCard: MovementCard, card: HTMLElement, stickyColumn: HTMLElement) {
        const cardRect = card.getBoundingClientRect();
        const stickyColumnRect = stickyColumn.getBoundingClientRect(); // most righthand sticky column
        const isIntersecting = cardRect.left <= stickyColumnRect.right;
        const hasIcon = parseInt(movementCard.style.left) > 0;

        let hiddenWidth = 0;
        if (isIntersecting) {
            // use the sticky column right pos - card's left pos to calculate the hidden width
            hiddenWidth = Math.max(0, stickyColumnRect.right - cardRect.left + (hasIcon ? -29 : 0)); // -29 = icon width + margin
        }

        movementCard.panelCollapsedHeadingText.element.style.transform = `translateX(${Math.round(hiddenWidth)}px)`;
    }

    private addTooltipHoversToRow(tableRow: TableRow) {
        const labelTractor = tableRow.findComponentById("labelTractorId") as Label;
        const labelDriver = tableRow.findComponentById("labelDriverName") as Label;
        const labelLastLoc = tableRow.findComponentById("labelLastLocation") as Label;
        const tractorId = tableRow.data.get("tractor", null);
        CommonTooltips.setTooltipContentLayout(labelTractor, tractorId, "lme/dispatch/asset/TractorQuickInfo");
        const driverId = tableRow.data.get("driver1_id", null)
        CommonTooltips.setTooltipContentLayout(labelDriver, driverId, "lme/dispatch/asset/DriverQuickInfo");
        labelLastLoc.tooltip = tableRow.data.get("last_location", null);
    }

    private addQuickInfosToMovementCard(movementCard: MovementCard) {
        const customerId = movementCard.data.get("customer_id", null);
        CommonTooltips.setTooltipContentLayout(movementCard.textboxCustomer, customerId, movementCard.textboxCustomer.quickInfoLayout);
    }

    private createTimeMarker() {
        if (document.getElementById("panelTimeMarkerLine") == null) {
            if (DateUtil.dateDiff(DatePart.DAY, DateUtil.setToBeginningOfDay(new Date()), this.startDate) >= 0 && DateUtil.dateDiff(DatePart.DAY, DateUtil.setToEndOfDay(new Date()), this.endDate) <= 0) {
                new TimeMarker(this.getHeaderCell(this.movementColumnIndex), this.calculator)
            }
        }
    }

    private createMovementCards(tableRow: TableRow, overlappedData: { [key: string]: any[] }) {
        const modelRow: ModelRow = tableRow.data;
        const timelineData = modelRow.get("timeline_data") ?? [];
        timelineData.forEach((preassignment: any, index: number) => {
            if (this.isDateWithinBounds(preassignment.start_date, preassignment.end_date)) {
                const id = `${modelRow.get("movement_id")}-${index}`;
                const movementData = new ModelRow(modelRow._modelPath, false, preassignment);
                const overlapIndex = this.findOverlapIndex(preassignment.id, overlappedData) ?? -1;
                this.createMovementCard(tableRow, id, movementData, overlapIndex);
            }
        });
    }

    private createMovementCard(tableRow: TableRow, identifier: string, data: ModelRow, overlapIndex: number) {
        const movementCardProps: Partial<LayoutProps> = { id: `movementCard-${identifier}` };
        const timelineCard = this.createTimelineCard(tableRow, data, overlapIndex, "lme/dispatch/planning/timeline/MovementCard", CardType.MOVEMENT, movementCardProps);
    }

    private createEventCards(tableRow: TableRow, overlappedData: { [key: string]: any[] }) {
        let events: any[] = tableRow.data?.get("driver1_timeline_data", null) ?? [];
        events.forEach((data: any) => {
            if (data.type == 1 && this.isDateWithinBounds(data.start_date, data.end_date)) {
                const eventData = new ModelRow(null, false, data);
                const overlapIndex = this.findOverlapIndex(eventData.get("id"), overlappedData) ?? -1;
                this.createEventCard(tableRow, eventData, overlapIndex);
            }
        });

        events = tableRow.data?.get("driver2_timeline_data", null) ?? [];
        events.forEach((data: any) => {
            if (data.type == 1 && this.isDateWithinBounds(data.start_date, data.end_date)) {
                const eventData = new ModelRow(null, false, data);
                const overlapIndex = this.findOverlapIndex(eventData.get("id"), overlappedData) ?? -1;
                this.createEventCard(tableRow, eventData, overlapIndex);
            }
        });
    }

    private createEventCard(tableRow: TableRow, data: ModelRow, overlapIndex: number) {
        const eventCardProps: Partial<LayoutProps> = {id: `eventCard-${data.get("id")}`};
        data.set("driver1_id", tableRow.data?.get("driver1_id", null));
        data.set("driver1_name", tableRow.data?.get("driver1_name", null));
        data.set("driver1_manager", tableRow.data?.get("driver1_manager", null));
        data.set("driver1_manager_name", tableRow.data?.get("driver1_manager_name", null));
        this.createTimelineCard(tableRow, data, overlapIndex, "lme/dispatch/planning/timeline/EventCard", CardType.EVENT, eventCardProps);
    }

    createTimelineCard(tableRow: TableRow, data: ModelRow, overlapIndex: number, layoutName: string, type: CardType, props: Partial<LayoutProps>) {
        const layoutProps: Partial<LayoutProps> = { height: this.bodyTableCellHeight, ...props };
        const timelineCard = new TimelineCard(layoutName, layoutProps, data, tableRow.data);
        timelineCard.overlap = overlapIndex != -1;
        this.timelineCards.push(timelineCard);
        timelineCard.layout.addLayoutLoadListener(() => {
            timelineCard.expanded = timelineCard.overlap ? tableRow.expanded : true;
            this.positionCardInRow(timelineCard, type, tableRow);
            if (timelineCard.layout instanceof MovementCard) {
                this.movementCardOnLoad(timelineCard.layout as MovementCard, tableRow, data);
                timelineCard.layout.panelEmpty.width = timelineCard.collapsedWidth;
            } else if (timelineCard.layout instanceof EventCard) {
                this.eventCardOnLoad(timelineCard.layout as EventCard);
            }
            if (timelineCard.overlap) {
                this.styleOverlap(timelineCard, overlapIndex, tableRow.expanded);
            }
            if (!timelineCard.overlap || tableRow.expanded) {
                this.setExpandedItemsVisibility(timelineCard);
            }
            timelineCard.layout.addClickListener(() => {
                if (timelineCard.selected) return;
                this.tableTimeline.selectedRow = tableRow;
                this.timelineCardOnSelect(timelineCard);
                if (this.shouldScrollToCard(timelineCard.layout))
                    this.scrollTo(timelineCard.layout.style.left as string);
            });
        });
        return timelineCard;
    }

    timelineCardOnSelect(selectedCard: TimelineCard) {
        this.selectedCard = selectedCard;
        this.timelineCards.forEach((tc: TimelineCard) => {
            tc.selected = selectedCard.layout.element == tc.layout.element && !selectedCard.selected;
            if (tc.layout instanceof MovementCard || tc.layout instanceof EventCard) {
                tc.layout.overlapSpacer.visible = !tc.expanded && tc.overlap;
                if (tc.layout instanceof MovementCard) {
                    tc.layout.panelEmpty.visible = !tc.data.getBoolean("loaded") && (!tc.overlap || tc.expanded);
                    tc.layout.panelExpandedButtons.visible = tc.selected;
                    tc.layout.panelMovement.style.position = tc.selected ? "absolute" : "relative";
                    tc.layout.panelMovement.style.opacity = tc.selected ? "1" : "0";
                    tc.layout.collapsedPanel.visible = tc.data.getBoolean("loaded") && (!tc.overlap || tc.expanded);
                    tc.layout.labelHeaderTrackingLate.visible = this.isTrackingBadgeVisible(tc.parentRow, tc.data) && !tc.selected;
                    tc.layout.displayStatus(this.arrived(tc.parentRow, tc.data), this.isFinalStop(tc.parentRow));
                } else {
                    tc.layout.collapsedPanel.visible = !tc.overlap || tc.expanded;
                    tc.layout.buttonClose.visible = tc.selected;
                    tc.layout.panelEvent.style.opacity = tc.selected ? "1" : "0";
                }
            }
        });
    }

    private movementCardOnLoad(movementCard: MovementCard, tableRow: TableRow, data: ModelRow) {
        movementCard.buttonClose.addClickListener(() => this.closeSelectedCard());
        this.setupOrderIdLabel(movementCard, data);
        this.setStopsLabel(movementCard.labelStopsPickDrop, data);
        this.addQuickInfosToMovementCard(movementCard);
        movementCard.contextMenuItems = this.getCardContextMenuItems(tableRow, data);
        movementCard.displayStatus(this.arrived(tableRow.data, data), this.isFinalStop(tableRow.data));
        this.applyTransitionProps(movementCard.panelMovement);
        const isNextAvailableMove = this.isNextAvailableMove(tableRow.data, data.get("origin_stop_id")) || this.isNextPreassignedMove(tableRow.data, data.get("id"));
        if (data.get("move_status") == "P" || (data.get("move_status") == "A" && isNextAvailableMove)) {
            this.setupETAandLate(movementCard, tableRow, data);
            if (isNextAvailableMove) {
                movementCard.labelEtaNextStop.text = "ETA to Pickup";
            }
        }
        if (!data.getBoolean("loaded")) {
            const emptyLineElement = movementCard.panelEmptyDash.element.children[0] as HTMLElement;
            emptyLineElement.style.backgroundImage = "linear-gradient(to right, gray 50%, transparent 50%)";
            emptyLineElement.style.backgroundSize = "20px 100%";
            emptyLineElement.style.height = "3px";
            movementCard.panelEmpty.visible = true;
            movementCard.panelEmpty.style.overflow = "hidden";
            movementCard.imageEmpty.style.overflow = "visible";
            movementCard.imageEmpty.visible = parseInt(movementCard.style.left) > 0;
            movementCard.panelEtaToNext.visible = movementCard.panelCommodity.visible =
            movementCard.textboxPieces.visible = movementCard.textboxCustomer.visible = false;
        }

        if (movementCard.panelCollapsed.element.offsetWidth/movementCard.panelHeadingText.element.offsetWidth > 3) {
            const existingIndex = this.largeElements.findIndex(e => e.card.data.get("id") == movementCard.data.get("id"));
            const largeElement = { card: movementCard, stickyColumnElement: tableRow.cells.at(-2).element };
            if (existingIndex == -1) {
                this.largeElements.push(largeElement);
            } else {
                this.largeElements[existingIndex] = largeElement;
            }
            this.checkCardIntersectionAndHiddenPortion(movementCard, movementCard.element, tableRow.cells.at(-2).element);
        }

        const dcMaxTrailers = getDispatchControlValue("maximum_trailers");
        if (dcMaxTrailers && !isNaN(dcMaxTrailers) && dcMaxTrailers < 3) {
            movementCard.textboxTrailer3.visible = false;
            movementCard.textboxDolly2.visible = false;
            if (dcMaxTrailers < 2) {
                movementCard.textboxTrailer2.visible = false;
                movementCard.textboxDolly1.visible = false;
            }
        }
        this.setupTrailerFont(movementCard, data, dcMaxTrailers);

        movementCard.collapsedStatus.style.overflow = "visible";
        movementCard.collapsedStatus.visible = parseInt(movementCard.style.left) > 0;
    }

    setupTrailerFont(movementCard: MovementCard, data: ModelRow<any>, dcMaxTrailers: number) {
        const trailerDropped = data.get("trailer_dropped", false);
        const trailer2Dropped = data.get("trailer2_dropped", false);
        const trailer3Dropped = data.get("trailer3_dropped", false);

        const updateLabelStyle = (textbox: Textbox, isDropped: boolean) => {
            if (textbox) {
                textbox.style.fontStyle = isDropped ? "italic" : "normal";
            }
        };

        updateLabelStyle(movementCard.textboxTrailer, trailerDropped);

        if (dcMaxTrailers > 1) {
            updateLabelStyle(movementCard.textboxTrailer2, trailer2Dropped);
        }

        if (dcMaxTrailers > 2) {
            updateLabelStyle(movementCard.textboxTrailer3, trailer3Dropped);
        }
    }

    private eventCardOnLoad(eventCard: EventCard) {
        this.applyTransitionProps(eventCard.panelEvent);
        eventCard.buttonClose.addClickListener(() => this.closeSelectedCard());
    }

    setExpandedItemsVisibility(timelineCard: TimelineCard) {
        const tc = timelineCard.layout;
        const collapsedPx = StyleUtil.remToPixels(parseFloat(timelineCard.collapsedWidth as string));
        if (tc instanceof MovementCard) {
            const visibleWidth = tc.panelHeadingText.element.offsetWidth;
            tc.panelHeadingText.visible = collapsedPx < (visibleWidth + 21); // image = 21px
            tc.imageStatus.visible = collapsedPx < 21;
            if (!tc.panelHeadingText.visible) {
                tc.panelTopRow.add(tc.panelMoveHeader);
            }
            if (!tc.imageStatus.visible)
                tc.panelMoveHeader.paddingLeft = 34;
        } else if (tc instanceof EventCard) {
            const visibleWidth = tc.labelCollapsedHeaderTitle.element.offsetWidth;
            tc.panelEventCardUpper.visible = collapsedPx < (visibleWidth + 21); // image = 21px
        }
    }

    closeSelectedCard() {
        if (this.selectedCard != null) {
            this.selectedCard.selected = false;
            const layout = this.selectedCard.layout;
            if (layout instanceof MovementCard || layout instanceof EventCard) {
                layout.overlapSpacer.visible = !this.selectedCard.expanded && this.selectedCard.overlap;
                if (layout instanceof MovementCard) {
                    layout.collapsedPanel.visible = (!this.selectedCard.overlap || this.selectedCard.expanded) && layout.data.getBoolean("loaded");
                    layout.panelEmpty.visible = (!this.selectedCard.overlap || this.selectedCard.expanded) && !layout.data.getBoolean("loaded");
                    layout.panelExpandedButtons.visible = false;
                    layout.labelHeaderTrackingLate.visible = this.isTrackingBadgeVisible(this.selectedCard.parentRow, layout.data);
                    layout.displayStatus(this.arrived(this.selectedCard.parentRow, this.selectedCard.data), this.isFinalStop(this.selectedCard.parentRow));
                    this.applyFadeOut(layout.panelMovement);
                } else {
                    layout.collapsedPanel.visible = !this.selectedCard.overlap || this.selectedCard.expanded;
                    layout.buttonClose.visible = false;
                    this.applyFadeOut(layout.panelEvent);
                }
            }
            this.selectedCard = null;
        }
    }

    private positionCardInRow(timelineCard: TimelineCard, type: CardType, tableRow: TableRow) {
        const arrivalPosition = this.calculator.getLeft(timelineCard.startDate);
        timelineCard.place(arrivalPosition, 0);
        timelineCard.collapsedHeight = StyleUtil.numberAsPixels(33);
        timelineCard.collapsedWidth = this.calculator.getWidth(arrivalPosition, timelineCard.endDate);
        tableRow.cells[this.movementColumnIndex].add(timelineCard.layout);
        timelineCard.layout.collapsedPanel.visible = (!timelineCard.overlap || timelineCard.expanded) && (type == CardType.MOVEMENT ? timelineCard.layout.data.getBoolean("loaded") : true);
        timelineCard.layout.collapsedPanel.width = timelineCard.layout.overlapSpacer.width = timelineCard.collapsedWidth;
        timelineCard.layout.visible = true;
    }

    styleOverlap(timelineCard: TimelineCard, overlapIndex: number, expanded: boolean) {
        if (timelineCard.layout instanceof MovementCard || timelineCard.layout instanceof EventCard) {
            if (timelineCard.layout instanceof MovementCard) {
                timelineCard.layout.panelEmpty.visible = expanded && !timelineCard.data.getBoolean("loaded");
                const originLate = timelineCard.data.getDateTime("origin_late", null);
                const originEarly = timelineCard.data.getDateTime("origin_early", null);
                const appointmentTime: Date = originLate && originLate?.getDate() ? originLate : originEarly;

                timelineCard.layout.panelOverlapSpacer.backgroundColor = this.getOverlapColor(
                    timelineCard.data.get("move_status"),
                    this.arrived(timelineCard.parentRow, timelineCard.data),
                    appointmentTime,
                    timelineCard.data.get("loaded")
                );
            } else {
                timelineCard.layout.panelEventCardUpper.visible = !expanded;
            }
            if (overlapIndex % 2 !== 0) {
                timelineCard.layout.top = expanded ? StyleUtil.numberAsRems(2.25) : StyleUtil.numberAsRems(1.25);
            }
            timelineCard.collapsedHeight = expanded ? 33 : 15;
            timelineCard.layout.panelOverlapSpacer.visible = !expanded;
        }
    }

    applyTransitionProps(panel: Panel) {
        panel.style.opacity = "0";
        panel.element.style.transition = "opacity 0.5s ease";
    }

    applyFadeOut(panel: Panel) {
        setTimeout(() => {
            panel.style.position = "relative";
            panel.element.style.opacity = "0";
        }, 0);
    }

    imageExpandOnClick(event: ClickEvent) {
        const imageExpand = event.target as Image;
        const tableRow = TableRow.getContainingTableRow(imageExpand);
        tableRow.expanded = !tableRow.expanded;
        imageExpand.rotation = tableRow.expanded ? 0 : -90;
        this.setupTimelineRow(tableRow);
    }

    setupExpanded(tableRow: TableRow) {
        tableRow.setExpandedAsync = async (value: boolean) => {
            tableRow["_expanded"] = value;
            return null;
        };
    }

    buttonSnapToOnClick() {
        this.scrollToToday();
    }

    private isDateWithinBounds(startDate: Date, endDate: Date): boolean {
        return DateUtil.dateDiff(DatePart.SECOND, endDate, this.startDate) >= 0 &&
            DateUtil.dateDiff(DatePart.SECOND, startDate, this.endDate) <= 0
    }

    private getTodayLeft(): number {
        return this.calculator.getLeft(DateUtil.setToBeginningOfDay(new Date()));
    }

    private scrollToToday() {
        const scrollPixels = StyleUtil.remToPixels(this.getTodayLeft());
        this.tableTimeline.adhocScroll({ left: scrollPixels, behavior: "instant" });
    }

    private scrollTo(leftRem: string) {
        const leftPos = StyleUtil.remToPixels(parseFloat(leftRem));
        this.tableTimeline.adhocScroll({ left: leftPos, behavior: "smooth" });
    }

    private setupProfileIdListeners() {
        this.textboxId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            if (this.profile != null && this.profile.get("id", "") === this.textboxId.text)
                event.filter = null;
        });
        this.textboxId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                const selectedId = this.textboxId.getFirstLookupModelData()?.get("id");
                if (selectedId !== this.profile?.get("id") || this.buttonFilter.imageName === ImageName.profileFiltered) {
                    this.adhocProfileFilterInUse = false;
                    this.loadProfile(selectedId);
                }
            }
        });
    }

    doAfterProfileSet(profile: RowOrderPlanningProfile) {
        if (profile != null) {
            this.textboxId.text = profile.get("id");
            this.buttonFilter.imageName = this.adhocProfileFilterInUse ? ImageName.profileFiltered : ImageName.profileFilter;
        }
        this.buttonFilter.visible = !this.isNested && profile != null;
    }

    buttonFilterOnClick() {
        Layout.getLayout("lme/dispatch/planning/OrderPlanningProfileFilter", {
            backgroundColor: "defaultBackground", borderShadow: true, paddingBottom: 50, fillHeight: true, width: 1125
        }).addLayoutLoadListener(event => {
            this.showFilter(event.target as OrderPlanningProfileFilter);
        });
    }

    showFilter(filterLayout: OrderPlanningProfileFilter) {
        filterLayout.mainDataSource.createBlankRow().then(row => {
            row._appending = false;
            row.setValues(this.profile.data);
            filterLayout.mainDataSource.data = [row];
            filterLayout.mainDataSource.setRowIndexWithoutUpdate(0);
            filterLayout.mainDataSource.mode = DataSourceMode.UPDATE;
            filterLayout.buttonCancel.addClickListener(() => Overlay.hideOverlay(overlay));
            filterLayout.buttonApply.addClickListener(() => {
                if (filterLayout.layoutProfile.mainDataSource.validate(true)) {
                    Overlay.hideOverlay(overlay);
                    if (row.hasChanged()) {
                        this.adhocProfileFilterInUse = true;
                        this.loadProfile(row);
                    }
                }
            });
            const overlay = Overlay.showInOverlay(filterLayout, { closeOnClickOff: false, greyedBackground: true, centered: true });
        });
    }

    filterTimelineItems(tableRow: TableRow) {
        const allData = this.getAllData(tableRow);
        const filterString = this.tableTimeline.filterValue;
        if (StringUtil.isEmptyString(filterString) || allData.length === 0) {
            return true;
        }

        const hasMatchingItem = allData.some((item: any) => {
            return Object.entries(item).some(([key, value]) => {
                if ((typeof value === "string" && value.toLowerCase().includes(filterString)) ||
                    (typeof value === "number" && value.toString().includes(filterString))) {
                    return this.isDateWithinBounds(item.start_date, item.end_date);
                }
                return false;
            });
        });

        return hasMatchingItem || this.tableTimeline["tableRowStringSearcher"].rowContains(tableRow.data, filterString);
    }

    tableSearcherCreationCallback(): TimelineComponentSearcher[] {
        return [
            new TimelineComponentSearcher("timeline_data"),
            new TimelineComponentSearcher("driver1_timeline_data"),
            new TimelineComponentSearcher("driver2_timeline_data")
        ];
    }

    public override async search(): Promise<any> {
        this.setCalendarFromProfile();
        const findNearProps = this.findNearProps;
        this.largeElements = [];
        super.search({ profile_row: this.profile, sort_for_timeline: true, ...findNearProps })
            .then(() => {
                // Add after search logic here
                this.zoomValidator.setValues(this.getDaysDisplayed(), false);
                this.setCalendarFromProfile();
                this.addDateValidations();
                this.initCalculator();
                this.configureTableInitial();
                this.initialConfigFinished = true;
                if (findNearProps) {
                    this.tableTimeline.clearSort();
                    this.tableTimeline.applySort("deadhead", "asc", true);
                }
            }).catch(error => log.debug("EquipMove search failed: %o", error));
    }

    getAllData(tableRow: TableRow) {
        const timelineData = tableRow.data.get("timeline_data") ?? [];
        const driver1Events = tableRow.data.get("driver1_timeline_data") ?? [];
        const driver2Events = tableRow.data.get("driver2_timeline_data") ?? [];
        return this.sortByDate(timelineData.concat(driver1Events).concat(driver2Events));
    }

    sortByDate(timelineData: any[]): any[] {
        return timelineData.sort((a, b) => {
            const diff = DateUtil.dateDiff(DatePart.SECOND, a.start_date, b.start_date);
            if (diff < 0) {
                return -1;
            } else if (diff > 0) {
                return 1;
            }
            return 0;
        });
    }

    getOverlapping(timelineData: any[]): { [key: string]: any[] } {
        const sortedData = timelineData.filter(item => this.isDateWithinBounds(item.start_date, item.end_date));
        const overlapGroups: { [key: string]: any[] } = {};
        let previous: any | null = null;

        for (const item of sortedData) {
            let addedToExistingGroup = false;

            // Try to add to an existing group
            for (const existingKey in overlapGroups) {
                const group = overlapGroups[existingKey];
                const lastMoveInGroup = group[group.length - 1];

                if (lastMoveInGroup && this.moveOverlaps(item, lastMoveInGroup)) {
                    group.push(item);
                    addedToExistingGroup = true;
                    break;
                }
            }

            // If it doesn't fit into any existing group AND overlaps with the previous move, create a new group
            if (!addedToExistingGroup && previous && this.moveOverlaps(item, previous)) {
                const key = this.getOverlapKey(previous.start_date);
                overlapGroups[key] = [previous];
                overlapGroups[key].push(item);
            }

            previous = item; // Keep track of the current move
        }

        return overlapGroups;
    }

    findOverlapIndex(id: string, overlappedData: { [key: string]: any[] }): number {
        for (const key in overlappedData) {
            const group = overlappedData[key];
            const index = group.findIndex(move => move.id === id);
            if (index != -1) {
                return index;
            }
        }
        return -1;
    }

    getOverlapKey(date: string) {
        return `${(new Date(date).getMonth()+1)}-${new Date(date).getDate()}-${new Date(date).getFullYear()}`;
    }

    getOverlapColor(status: string, arrived: boolean, appointment_time: Date, loaded: boolean): string {
        if (!loaded) {
            return "subtle";
        }
        switch (status) {
            case "P": return arrived ? "warning.darker" : "#054075";
            case "A": {
                if (!arrived) {
                    const hoursTillPickup = DateUtil.dateDiff(DatePart.HOUR, appointment_time, new Date());
                    const pickupWarningTimeHours: number = getDispatchControlValue("pickup_warning");
                    return hoursTillPickup < 0
                        ? "error"
                        : hoursTillPickup <= pickupWarningTimeHours
                            ? "caution"
                            : "#B8D1A8";
                }
                return "warning.darker";
            }
            case "D": return "#F2E6F2";
        }
    }

    /*
    * Checks for overlaps between two moves (could include driver events)
    * move1: the next move to be displayed
    * move2: the previous move displayed
    */
    moveOverlaps(move1: any, move2: any): boolean {
        const isOverlapping = DateUtil.dateDiff(DatePart.SECOND, move1.start_date, move2.end_date) < 0 &&
            DateUtil.dateDiff(DatePart.SECOND, move1.end_date, move2.start_date) > 0;
        if (move1.type == 0 && move2.type == 0 && move1.move_status != "A" && move2.move_status != "A") {
            // if last move is D/P and next move is E, and the last move's end date overlaps E's start date
            if (move2.loaded && !move1.loaded && DateUtil.dateDiff(DatePart.SECOND, move2.end_date, move1.start_date) > 0) {
                move1.start_date = move2.end_date;
            }
            // if last move is E and next move is D/P/E, and the last move's dest actual departure overlaps D/P's start date
            if (!move2.loaded && DateUtil.dateDiff(DatePart.SECOND, move2.destination_actual_departure, move1.start_date) > 0) {
                move2.end_date = move1.start_date;
            }
            return false;
        }
        return isOverlapping;
    }

    textboxHoursAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.userInitiatedChange)
            this.redisplay(true);
    }

    redisplay(updateHours: boolean = false) {
        this.initialCellLoaded = false;
        this.calculateLeftPostitions(true);
        this.zoomValidator.setValues(this.getDaysDisplayed(), !updateHours);
        this.initCalculator();
        this.tableTimeline.dataSource.displayDataInBoundComponents();
        this.resetForDates();
    }

    getDaysDisplayed() {
        // Calculator can be out of sync with the start and end date therefore use this if calculator is not yet initalized
        return DateUtil.dateDiff(DatePart.DAY, this.endDate, this.startDate) + 1;
    }

    private buttonHoursOfServiceOnClick(event: ClickEvent) {
        PlanningUtil.showHoursOfServiceSlideout(event, "driver1_id");
    }

    private buttonMcMessagesOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const modelRow = tableRow.data as ModelRow;
        MobileCommMessages.openSlideout({
            movementId: modelRow.get("movement_id"),
            tractorId: modelRow.get("tractor")
        });
    }

    private handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const source = event.target as DataSource;
        const changedRow = event.change.data as ModelRow;
        const index = source.findRowInData(changedRow);
        const currentRow = index >= 0 ? source.data[index] : null;

        this.testUpdateFreshness(event, currentRow);
        if (event.defaultPrevented !== true) {
            if (this.findNearProps != null && currentRow != null) {
                changedRow.set("deadhead", currentRow.get("deadhead"));
            }
            this.adjustEventType(event, currentRow);
        }
    }

    getCardContextMenuItems(tableRow: TableRow, data: ModelRow): Partial<ContextMenuLabelProps>[] {
        return OrderTabView.addNavigationContextMenuItems([
            {
                text: "Dispatch", visible: true, imageName: ImageName.send,
                onClick: () => {
                    this.tableTimeline.selectedRow = tableRow;
                    this.getToolsPanel().buttonDispatch.clicked();
                },
            },
            {
                text: `Open`, visible: true,
                onClick: () => this.showOrder(data.get("dispatch_id"), data.get("id")),
            },
        ], data.get("dispatch_id"), data.get("id"));
    }

    showOrder(order_id: string, movement_id: string = null) {
        OrderTabView.slideUp({
            key: (movement_id ? movement_id : order_id),
            ...(movement_id ? { orderId: order_id } : {})
        });
    }

    setupOrderIdLabel(movementCard: MovementCard, modelRow: ModelRow) {
        if (!modelRow.isNull("dispatch_id")) {
            const orderIdLabel = movementCard.findComponentById("labelOrderId") as Label;
            orderIdLabel.addUnderlineWhenMouseOver();
            orderIdLabel.addClickListener((event: ClickEvent) => {
                PlanningUtil.navigateToOrder(event);
            });
        }
    }

    setStopsLabel(pickDropLabel: Label, modelRow: ModelRow) {
        let text = modelRow.get("stops_pick_drop");
        if (!text) return;
        text = text.replace(/,\s/, ',\n');
        pickDropLabel.text = text;
        pickDropLabel.addUnderlineWhenMouseOver();
        pickDropLabel.addClickListener((event) => PlanningUtil.showMultiStopSlideout(event.target as Label, true, modelRow.get("dispatch_id")));
    }

    setupETAandLate(movementCard: MovementCard, tableRow: TableRow, data: ModelRow) {
        const isNextAvailableMove = this.isNextAvailableMove(tableRow.data, data.get("origin_stop_id"));
        const isNextPreassignedMove = this.isNextPreassignedMove(tableRow.data, data.get("id"));
        data.setValues({
            dist_to_next_stop: tableRow.data.get("dist_to_next_stop"),
            next_stop_eta_time: tableRow.data.get(isNextPreassignedMove ? "np_stop_eta_time" : "next_stop_eta_time"),
            tracking_late_minutes: tableRow.data.get("tracking_late_minutes"),
            tracking_late_string: tableRow.data.get("tracking_late_string"),
        });
        const visible = !data.isNull("dist_to_next_stop") || !data.isNull("next_stop_eta_time");
        movementCard.panelEtaToNext.visible = !isNextAvailableMove && isNextPreassignedMove ? !data.isNull("next_stop_eta_time") : visible;
        movementCard.panelEtaToNext.displayData(data, null, 0);
        if (!isNextAvailableMove && isNextPreassignedMove) { // only care for next preassigned move when current is in progrss
            movementCard.labelDistToNextStop.visible = movementCard.labelHeaderTrackingLate.visible = movementCard.labelTrackingLateBadge.visible = false;
        } else {
            if (!tableRow.data.isNull("next_stop_actual_arrival")
                && tableRow.data.isNull("next_stop_actual_departure")) {
                movementCard.labelArrived.visible = true;
                movementCard.labelDistToNextStop.visible = false;
            }
            movementCard.labelHeaderTrackingLate.text = data.get("tracking_late_string");
            movementCard.labelHeaderTrackingLate.visible = this.isTrackingBadgeVisible(tableRow.data, data);
            movementCard.labelNextStopLateStatus.addClickListener(() => this.labelNextStopLateStatusOnClick());
            if (!tableRow.data.isNull("next_stop_late_status", null))
                PlanningUtil.setupLateStatus(movementCard.panelNextStopLateStatus, tableRow.data);
        }
    }

    private addHoursOfServiceHover(tableRow: TableRow) {
        const hoursButton = tableRow.findComponentById("buttonHoursOfService") as Button;
        if (hoursButton != null) {
            const modelRow = tableRow.data;
            if (!modelRow.isNull("driver1_id")) {
                CommonTooltips.setTooltipContentLayout(
                    hoursButton,
                    modelRow.getString("driver1_id"), "lme/dispatch/asset/DriverHosQuickInfo"
                );
                return;
            }
        }
        hoursButton.visible = false;
    }

    isTrackingBadgeVisible(equipMove: ModelRow, data: ModelRow): boolean {
        return (data.get("move_status") == "P" || this.isNextAvailableMove(equipMove, data.get("origin_stop_id"))) && !data.isNull("tracking_late_string");
    }

    arrived(equipMove: ModelRow, cardData: ModelRow): boolean {
        return (cardData.get("move_status") == "P" && !equipMove.isNull("next_stop_actual_arrival")) || (cardData.get("id") == equipMove.get("np_move_id") && !equipMove.isNull("np_origin_actual_arrival"));
    }

    isFinalStop(equipMove: ModelRow): boolean {
        return equipMove.get("next_stop_stop_id") == equipMove.get("destination_stop_id");
    }

    isNextAvailableMove(equipMove: ModelRow, originStopId: string): boolean {
        return equipMove.get("next_stop_stop_id") == originStopId;
    }

    isNextPreassignedMove(equipMove: ModelRow, moveId: string): boolean {
        return equipMove.get("np_move_id") == moveId;
    }

    /** This is an event handler for the onClick event of labelNextStopLateStatus. */
    async labelNextStopLateStatusOnClick() {
        let moveId: string;
        if(this.selectedCard.parentRow.get("move_status") === "D") {
            moveId = this.selectedCard.parentRow.get("np_move_id");
        }
        else {
            moveId = this.selectedCard.parentRow.get("movement_id");
        }
        const response = await Api.search("lme/dispatch/planning/late-route", {
            "movement_id": moveId,
            "is_current": "Y"
        });
        new LateRouteViewSlideout({
            movementId: moveId,
            stopId: this.selectedCard.parentRow.get("next_stop_stop_id"),
            isCurrent: response.actual_row_count > 0
        });
    }

    private setupDeadHeadListeners() {
        const columnDeadhead = this.tableTimeline.getColumnByCaption("Deadhead");
        const sortSelector = columnDeadhead.headingCell.getSortSelector();
        sortSelector.addClickListener(() => sortSelector.getFieldLabel().color = "success");
        sortSelector.getFieldLabel().color = "success";
    }

    private setDeadInfo(tableRow: TableRow, modelRow: ModelRow) {
        if (modelRow.isNull("deadhead")) return;
        const labelDeadhead = tableRow.findComponentById("labelDeadhead")
        const getMethodDisplayValue = () => {
            switch (getDispatchControlValue("findnear_method")) {
                case "S": return "Straight Line";
                case "M": return "Move Miles";
                case "F": return "Fuel Miles";
            }
        }

        CommonTooltips.setTooltipContentTextboxes(labelDeadhead, () => [
            {
                caption: "Deadhead From",
                text: modelRow.getString("deadhead_from_loc")
            },
            {
                caption: "Pulled From",
                text: modelRow.getString("deadhead_from")
            },
            {
                caption: "Distance Method",
                text: getMethodDisplayValue()
            }
        ]);
    }

    private shouldScrollToCard(card: TimelineCardLayout): boolean {
        const scrollContainer = this.tableTimeline["_tbody"];
        const scrollLeft = scrollContainer.scrollLeft;
        const containerWidth = scrollContainer.clientWidth;

        const cardLeft = card.element.offsetLeft;
        const cardRight = cardLeft + card.element.offsetWidth;
        const cardWidth = card.element.offsetWidth;

        const visibleLeft = Math.max(cardLeft, scrollLeft);
        const visibleRight = Math.min(cardRight, scrollLeft + containerWidth);
        const visibleWidth = Math.max(0, visibleRight - visibleLeft);

        const visibilityPercentage = (visibleWidth / cardWidth) * 100;
        return cardLeft < scrollLeft;
    }

    initTimer() {
        const callback = () => {
            this.timelineCards.forEach((tc: TimelineCard) => {
                if (tc.layout instanceof MovementCard) {
                    tc.layout.displayStatus(this.arrived(tc.parentRow, tc.data), this.isFinalStop(tc.parentRow));
                }
            });
        };
        this.tableTimeline.addMountListener(() => this.startMinuteTimer(callback, this.tableTimeline));
    }
}
MixinUtil.applyMixin(OrderPlanningTimeline, OrderPlanningLayout);

// TypeScript interface declaration for mixin integration
export interface OrderPlanningTimeline extends OrderPlanningLayout {
}
